import { z } from "zod/v4";
import { CameraSubject, defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { stringToInt } from "@visioni/a3s-codecs";
import { TestSignalPathSchema } from "../g2-fleet-messages/test-signal-metadata";
import { FEATURE_NAME } from "../feature-name";

/**
 * The delivery legs a test signal can exercise.
 *
 * Re-exported from the wire metadata contract rather than restated, so the
 * legs the API accepts cannot drift from the ones a stored clip can be tagged
 * with. Adding a leg is a one-line change there; every exhaustive `match` on
 * this type then fails to compile until it handles the new one.
 *
 * `TEST_SIGNAL_PATHS` is derived from the schema so the UI can offer one action per
 * leg without restating the list either.
 */
export const TestSignalPath = TestSignalPathSchema;
export type TestSignalPath = z.output<typeof TestSignalPath>;
export const TEST_SIGNAL_PATHS = TestSignalPath.options;

/**
 * Defaulted for the same reason the command defaults it: a client that omits
 * the field means the camera leg, which is the only one every camera has.
 */
export const TriggerTestSignalBody = z.object({
  signalPath: TestSignalPath.default("camera"),
});
export type TriggerTestSignalBody = z.output<typeof TriggerTestSignalBody>;

/**
 * The publish receipt, matching `TestSignalTriggerReceipt` from
 * `../client-services/test-signals-service`.
 *
 * `topic` and `publishedAt` are what correlate the request with the
 * `"test signal command received"` line on the device. Neither proves the
 * camera acted — see the receipt type's own doc comment.
 */
export const TriggerTestSignalResponse = z.object({
  topic: z.string(),
  publishedAt: z.string(),
});
export type TriggerTestSignalResponse = z.output<typeof TriggerTestSignalResponse>;

/**
 * Camera-scoped — the scope `defineResource` defaults to. The command topic
 * addresses one installed camera, so the camera belongs in the path rather
 * than the body (ADR-0031).
 */
export const testSignalsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "test-signals",
});

export const triggerTestSignalEndpoint = defineEndpoint(testSignalsResource, {
  method: "POST",
  bodySchema: TriggerTestSignalBody,
  responseSchema: TriggerTestSignalResponse,
});

/**
 * Query params for listing a camera's test signal log.
 *
 * `limit` bounds the page (1-100); `cursor` is the opaque ElectroDB cursor from
 * the previous page's response. No time range: the list is scoped to one camera
 * slot and paged newest-first, so callers page with the cursor rather than a
 * window. Mirrors `a3s-dumping`'s confirmed-dumps listing.
 *
 * `limit` defaults rather than being optional all the way down. Left unset the
 * query carries no `Limit` at all and DynamoDB answers with a full 1MB page —
 * thousands of these rows — so a caller who simply omits it would get the one
 * page size nobody chose. The cap only bounds callers who already thought about
 * it; the default bounds the rest.
 */
export const ListTestSignalLogQueryParams = z.object({
  limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
  cursor: z.string().optional(),
});
export type ListTestSignalLogQueryParamsWire = z.input<typeof ListTestSignalLogQueryParams>;
export type ListTestSignalLogQueryParams = z.output<typeof ListTestSignalLogQueryParams>;

/**
 * What a log entry records — the wire counterpart of the stored `kind`.
 *
 * `command` is the word the rest of the stack uses for the thing an operator
 * sends (`TestSignalCommandV1`, the `.../test-signal/commands` topic, the
 * device's "test signal command received" line). Deliberately not "trigger":
 * `triggerType` already owns that word for a different distinction.
 */
export const TestSignalLogKind = z.enum(["command", "signal"]);
export type TestSignalLogKind = z.output<typeof TestSignalLogKind>;

/**
 * The pipeline's verdict on an entry.
 *
 * Kept as a plain string union on the wire rather than re-deriving the storage
 * layer's `TEST_SIGNAL_LOG_OUTCOMES`: `client-messages` is imported by the app,
 * and reaching into `services/` from here would drag the ElectroDB entity — and
 * the AWS SDK behind it — into the frontend bundle. The server projects onto
 * this union with an exhaustive match, so a new outcome fails to compile there.
 */
export const TestSignalLogOutcome = z.enum([
  "sent",
  "accepted",
  "deferred",
  "delivered",
  "duplicate",
  "dropped-no-provider",
  "dropped-unmonitored-asset",
  "dropped-missing-context",
  "dropped-unintegrated-provider",
  "dropped-non-v2-provider",
  "poisoned",
  "expired",
]);
export type TestSignalLogOutcome = z.output<typeof TestSignalLogOutcome>;

/** A command row: a test signal an operator asked a camera for. */
export const TestSignalLogCommandItem = z.object({
  kind: z.literal("command"),
  subject: CameraSubject,
  occurredAt: z.iso.datetime(),
  recordId: z.string(),
  signalPath: TestSignalPath,
  outcome: z.literal("sent"),
  /** Support user who sent it, as the stable `UserId`. */
  actor: z.string(),
  /**
   * The actor's email, for an operator reading the table — a `UserId` names
   * nobody. Resolved at read time from the Auth0 user cache, so it is never
   * stored on the log row and cannot go stale.
   *
   * Absent when the cache has no entry for that user, or where the realm has
   * no cache configured; the UI falls back to `actor`.
   */
  actorEmail: z.string().optional(),
  /** IoT topic published to; ties the row to the device's own log line. */
  topic: z.string(),
});

/** A signal row: what the pipeline did with a signal a camera produced. */
export const TestSignalLogSignalItem = z.object({
  kind: z.literal("signal"),
  subject: CameraSubject,
  occurredAt: z.iso.datetime(),
  recordId: z.string(),
  signalPath: TestSignalPath,
  outcome: TestSignalLogOutcome,
  triggerType: z.enum(["manual", "periodic"]).optional(),
  vendor: z.enum(["axis", "mobotix"]).optional(),
  /** Dispatch provider, e.g. `visioni_v2`. Absent on rows dropped at intake. */
  provider: z.string().optional(),
  /** When the provider acknowledged delivery. */
  deliveredAt: z.iso.datetime().optional(),
  latencyMs: z.number().optional(),
  /** Delivery attempts that failed before this outcome. */
  failureCount: z.number().optional(),
});
export type TestSignalLogSignalItem = z.output<typeof TestSignalLogSignalItem>;
export type TestSignalLogCommandItem = z.output<typeof TestSignalLogCommandItem>;

/**
 * One row of a camera's test signal log.
 *
 * A discriminated union on `kind`, which is what keeps the storage layer's
 * optional attributes from leaking: one entity holds both shapes, so
 * command-only and signal-only columns are nullable there, but a reader here
 * never sees a field that does not belong to the row it has.
 *
 * Timestamps are raw ISO — the app formats them in the viewer's locale, as
 * every other list endpoint in this codebase does.
 */
export const TestSignalLogItem = z.discriminatedUnion("kind", [TestSignalLogCommandItem, TestSignalLogSignalItem]);
export type TestSignalLogItem = z.output<typeof TestSignalLogItem>;

/**
 * Standard a3s `{ data, cursor? }` envelope, newest-first. Hand-rolled rather
 * than built with the `CollectionResponse` factory, which is typed for a plain
 * `ZodObject` item and cannot take a discriminated union — the same reason
 * `a3s-reports` builds its own. The `subject: CameraSubject` the factory would
 * have required is on both union members regardless.
 */
export const ListTestSignalLogResponse = z.object({
  data: z.array(TestSignalLogItem),
  cursor: z.string().optional(),
});
export type ListTestSignalLogResponse = z.output<typeof ListTestSignalLogResponse>;

/**
 * Its own resource, deliberately not a `GET` on `testSignalsResource`.
 *
 * A `GET` beside that `POST` would read as "the collection of things the POST
 * creates", and this is not that. The `POST` publishes a command to a device —
 * it creates no resource at all — while this returns the pipeline's *log*,
 * whose rows are mostly signals nobody asked for (the periodic heartbeat) and
 * outcomes rather than created objects. Sharing the path would make the URL
 * claim a relationship that does not hold, and leave two very different
 * contracts distinguishable only by method in access logs and traces.
 *
 * Splitting costs nothing: the no-CDK-change property comes from the greedy
 * `/features/monitoring` proxy (`addFeatureRoute`), which carries any resource
 * under the subtree — not from sharing a path with the trigger.
 */
export const testSignalLogResource = defineResource({
  feature: FEATURE_NAME,
  resource: "test-signal-log",
});

export const listTestSignalLogEndpoint = defineEndpoint(testSignalLogResource, {
  responseSchema: ListTestSignalLogResponse,
  querySchema: ListTestSignalLogQueryParams,
});

/**
 * The newest signal a camera produced per signal path and trigger type, and the
 * newest command sent per signal path, within a fixed window.
 *
 * Exists because the log is paged: on a busy camera the last periodic VA signal
 * can sit pages back, so a summary worked out from the loaded rows would wrongly
 * say there was none. `since` is how far back the server looked, so a missing
 * cell reads as "none since …" rather than "never".
 */
export const LatestTestSignalsResponse = z.object({
  since: z.iso.datetime(),
  data: z.array(TestSignalLogItem),
});
export type LatestTestSignalsResponse = z.output<typeof LatestTestSignalsResponse>;

/** Its own resource beside the log, for the same reason the log is not a GET on the trigger. */
export const latestTestSignalsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "latest-test-signals",
});

export const latestTestSignalsEndpoint = defineEndpoint(latestTestSignalsResource, {
  responseSchema: LatestTestSignalsResponse,
});
